import React from "react";
import { Link } from "react-router-dom";
import {
    LayoutDashboard, UtensilsCrossed, Sparkles, Inbox, Settings, FileText, Users, LogOut, ExternalLink,
} from "lucide-react";

export const ADMIN_NAV = [
    { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { key: "menu", label: "Menu", icon: UtensilsCrossed },
    { key: "specials", label: "Specials & Bundles", icon: Sparkles },
    { key: "orders", label: "Orders & Enquiries", icon: Inbox, badge: "inbox" },
    { key: "settings", label: "Site Settings", icon: Settings },
    { key: "content", label: "Page Content", icon: FileText },
    { key: "users", label: "Admin Users", icon: Users },
];

function NavLink({ item, active, badge, compact }) {
    const Icon = item.icon;
    const isActive = active === item.key;
    return (
        <Link
            ref={(el) => { if (el && isActive && compact) el.scrollIntoView({ block: "nearest", inline: "center" }); }}
            to={`/admin/${item.key}`}
            data-testid={`admin-nav-${item.key}`}
            className={`group flex items-center gap-3 rounded-sm transition-colors whitespace-nowrap ${
                compact ? "px-3 py-2 text-xs border" : "px-4 py-2.5 text-sm border-l-2"
            } ${
                isActive
                    ? "border-brand-gold text-brand-gold bg-brand-gold/5"
                    : "border-transparent text-brand-cream/70 hover:text-brand-cream hover:bg-white/[0.03]"
            }`}
        >
            <Icon size={compact ? 14 : 16} className={isActive ? "text-brand-gold" : "text-brand-mute group-hover:text-brand-cream"} />
            <span className="flex-1">{item.label}</span>
            {badge > 0 && (
                <span data-testid={`admin-nav-badge-${item.key}`} className="text-[10px] bg-brand-gold text-brand-ink px-1.5 py-0.5 rounded-sm font-semibold">
                    {badge}
                </span>
            )}
        </Link>
    );
}

export default function AdminSidebar({ active, me, onLogout, inboxCount }) {
    const badgeFor = (item) => (item.badge === "inbox" ? inboxCount : 0);
    return (
        <>
            <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-[#0D0D0D] border-r border-brand-line z-40" data-testid="admin-sidebar">
                <div className="px-6 pt-7 pb-6 border-b border-brand-line">
                    <p className="font-script text-brand-gold text-2xl leading-none">Umami Kraft</p>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-brand-mute mt-2">Kitchen Command</p>
                </div>
                <nav className="flex-1 py-5 px-3 space-y-1 overflow-y-auto">
                    {ADMIN_NAV.map((item) => (
                        <NavLink key={item.key} item={item} active={active} badge={badgeFor(item)} />
                    ))}
                </nav>
                <div className="p-4 border-t border-brand-line space-y-2">
                    <p className="text-xs text-brand-mute truncate" data-testid="admin-current-user">{me?.email}</p>
                    <div className="flex gap-2">
                        <a href="/" target="_blank" rel="noreferrer" data-testid="admin-view-site" className="btn-ghost text-xs flex-1 inline-flex items-center justify-center gap-1.5 !px-2 !py-2">
                            <ExternalLink size={12} /> View site
                        </a>
                        <button data-testid="admin-logout" onClick={onLogout} className="btn-ghost text-xs flex-1 inline-flex items-center justify-center gap-1.5 !px-2 !py-2">
                            <LogOut size={12} /> Sign out
                        </button>
                    </div>
                </div>
            </aside>

            <div className="lg:hidden sticky top-0 z-40 bg-[#0D0D0D] border-b border-brand-line" data-testid="admin-mobile-nav">
                <div className="flex items-center justify-between px-5 py-3">
                    <p className="font-script text-brand-gold text-xl leading-none">Umami Kraft</p>
                    <button data-testid="admin-logout-mobile" onClick={onLogout} className="text-xs text-brand-mute inline-flex items-center gap-1.5">
                        <LogOut size={12} /> Sign out
                    </button>
                </div>
                <nav className="flex gap-2 px-5 pb-3 overflow-x-auto">
                    {ADMIN_NAV.map((item) => (
                        <NavLink key={item.key} item={item} active={active} badge={badgeFor(item)} compact />
                    ))}
                </nav>
            </div>
        </>
    );
}
